// src/encounter/visual-acuity-dialog.tsx
// Visual-acuity capture dialog (PROD-1537) — the "+ Visual Acuity" affordance opens this. Composes
// DS Modal + VisualAcuityForm; validates on Save (Flutter's per-pair isPairInvalid sweep) and emits
// the VisualAcuityData for the host to persist (buildVisualAcuityRequest → useSaveVitals).
import { useState } from 'react';
import { Banner, Modal, Button } from '@scripsteam/scrips-signal-ds';
import { VisualAcuityForm } from './visual-acuity-form';
import {
  type VisualAcuityData,
  emptyVisualAcuity,
  validateVisualAcuity,
  isVisualAcuityEmpty,
} from './lib/visual-acuity';

export interface VisualAcuityDialogProps {
  open: boolean;
  /** Pre-fill (edit) — the existing visual-acuity extraData, already mapped to VisualAcuityData. */
  existing?: VisualAcuityData | null;
  saving?: boolean;
  onSubmit: (value: VisualAcuityData) => void;
  onClose: () => void;
}

/** Outer gate + keyed remount so each open re-initialises from `existing`. */
export function VisualAcuityDialog(props: VisualAcuityDialogProps) {
  if (!props.open) return null;
  return <VisualAcuityDialogBody key={isVisualAcuityEmpty(props.existing ?? emptyVisualAcuity()) ? 'add' : 'edit'} {...props} />;
}

function VisualAcuityDialogBody({ existing, saving, onSubmit, onClose }: VisualAcuityDialogProps) {
  const [data, setData] = useState<VisualAcuityData>(() => existing ?? emptyVisualAcuity());
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    if (isVisualAcuityEmpty(data)) {
      setError('Enter at least one visual-acuity value.');
      return;
    }
    const err = validateVisualAcuity(data);
    if (err) {
      setError(err);
      return;
    }
    onSubmit(data);
  }

  return (
    <Modal open onClose={onClose} title="Visual acuity" size="lg">
      <VisualAcuityForm
        value={data}
        onChange={(next) => {
          setData(next);
          setError(null);
        }}
      />
      {error ? <Banner variant="error" role="alert" title="Not saved">{error}</Banner> : null}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="tertiary" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : 'Done'}
        </Button>
      </div>
    </Modal>
  );
}
